import { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { GET_CIRCLE_MEDIA_URL } from '@/api/graphql/queries';
import type { MediaIdResult, QuestionMedia as QuestionMediaItem } from '@/types/api';
import { MEDIA_MAX_VH, clampToWindow, clipWindow, mediaLabel, resultChips } from '../questionMedia';

/**
 * A Backstory question's item, above the question: the family photo, home
 * movie, recording or document The Archivist is asking about, signed for the
 * signed-in family member. A clip or recording plays only when they tap it
 * (iOS needs the tap) and on its own element, never the shared one the
 * interviewer speaks through; it pauses while The Archivist speaks or they
 * record. After an answer, what was understood shows as chips under it.
 */
export function QuestionMedia({
  media,
  result,
  hold,
}: {
  media: QuestionMediaItem;
  result?: MediaIdResult | null;
  /** The Archivist is speaking or they're recording: keep a clip quiet. */
  hold: boolean;
}) {
  const { data, error } = useQuery(GET_CIRCLE_MEDIA_URL, {
    variables: { circleId: media.circleId, sourceType: media.sourceType, sourceId: media.sourceId },
    // Links are short-lived: sign each item as it comes up.
    fetchPolicy: 'network-only',
  });
  const url: string | null = data?.getCircleMediaUrl?.success
    ? data.getCircleMediaUrl.downloadUrl
    : null;
  const failed = !!error || data?.getCircleMediaUrl?.success === false;
  const player = useRef<HTMLVideoElement & HTMLAudioElement>(null);

  useEffect(() => {
    if (hold) player.current?.pause();
  }, [hold]);

  const chips = resultChips(result);
  const label = mediaLabel(media);
  const frame: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: `${MEDIA_MAX_VH}vh`,
    borderRadius: 10,
    boxShadow: '0 10px 30px rgba(26, 31, 26, 0.18)',
    background: 'var(--lp-paper-2)',
  };

  const keepToWindow = () => {
    const el = player.current;
    if (!el) return;
    const { end } = clipWindow(media);
    if (end != null && el.currentTime >= end) {
      el.pause();
      el.currentTime = clampToWindow(end, media);
    }
  };
  const startAtWindow = () => {
    const el = player.current;
    if (el && el.currentTime < clipWindow(media).start) el.currentTime = clipWindow(media).start;
  };

  let body: React.ReactNode;
  if (media.mediaType === 'DOCUMENT') {
    body = (
      <blockquote
        className="lp-serif"
        style={{
          ...frame,
          margin: 0,
          padding: '14px 18px',
          overflowY: 'auto',
          textAlign: 'left',
          fontSize: 15,
          lineHeight: 1.45,
          color: 'var(--lp-ink-deep)',
          background: 'var(--lp-paper-warm)',
        }}
      >
        {media.excerpt || label}
      </blockquote>
    );
  } else if (!url) {
    body = (
      <div
        role="img"
        aria-label={label}
        style={{
          ...frame,
          width: 'min(100%, 420px)',
          height: `${Math.round(MEDIA_MAX_VH * 0.7)}vh`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--lp-ink-mute)',
          fontSize: 13,
        }}
      >
        {failed ? label : null}
      </div>
    );
  } else if (media.mediaType === 'VIDEO') {
    body = (
      <video
        ref={player}
        src={url}
        controls
        playsInline
        preload="metadata"
        aria-label={label}
        onLoadedMetadata={startAtWindow}
        onPlay={startAtWindow}
        onTimeUpdate={keepToWindow}
        style={{ ...frame, width: 'min(100%, 560px)', background: '#000' }}
      />
    );
  } else if (media.mediaType === 'AUDIO') {
    body = (
      <div style={{ width: 'min(100%, 460px)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <audio
          ref={player}
          src={url}
          controls
          preload="metadata"
          aria-label={label}
          onLoadedMetadata={startAtWindow}
          onTimeUpdate={keepToWindow}
          style={{ width: '100%' }}
        />
        {media.excerpt ? (
          <p
            className="lp-serif"
            style={{
              margin: 0,
              maxHeight: '14vh',
              overflowY: 'auto',
              fontSize: 14,
              lineHeight: 1.4,
              color: 'var(--lp-ink-soft)',
              textAlign: 'left',
            }}
          >
            {media.excerpt}
          </p>
        ) : null}
      </div>
    );
  } else {
    body = <img src={url} alt={label} style={{ ...frame, objectFit: 'contain' }} />;
  }

  return (
    <figure
      data-question-media="true"
      style={{
        margin: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
      }}
    >
      {body}
      {chips.length ? (
        <ul
          aria-label="What The Archivist heard"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 6,
            maxWidth: 560,
          }}
        >
          {chips.map(chip => (
            <li
              key={chip}
              style={{
                padding: '3px 10px',
                borderRadius: 999,
                border: '1px solid var(--lp-line-strong)',
                background: 'var(--lp-paper-warm)',
                fontSize: 12,
                color: 'var(--lp-ink-deep)',
              }}
            >
              {chip}
            </li>
          ))}
        </ul>
      ) : null}
    </figure>
  );
}
